import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Camera, Building2, FileText, Crown, KeyRound, ExternalLink } from "lucide-react";

const PLAN_CLS = {
  Trial: "text-ai", Gratuito: "text-muted-foreground", Plus: "text-chart-3",
  "Médico Profissional": "text-ai", Premium: "text-success", blocked: "text-destructive",
};

function PlanCard() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/me/plan").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  const lim = p.limits || {};
  const use = p.usage || {};
  const fmt = (u, c) => `${u ?? 0} / ${c == null ? "∞" : c}`;
  return (
    <Card className="p-6 border-border shadow-none mb-6" data-testid="plan-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Crown className={`w-5 h-5 ${PLAN_CLS[p.plan] || ""}`} />
          <div>
            <p className="font-heading font-medium">Meu plano: <span className={PLAN_CLS[p.plan]}>{p.plan === "blocked" ? "Bloqueado" : p.plan}</span></p>
            {p.expires_at && <p className="text-xs text-muted-foreground">Válido até {p.expires_at.slice(0, 10)}{p.days_left != null ? ` · ${p.days_left} dia(s)` : ""}</p>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="rounded-md border border-border p-3"><p className="text-xs text-muted-foreground">Pacientes</p><p className="font-mono-data font-semibold mt-1">{fmt(use.patients, lim.patients)}</p></div>
        <div className="rounded-md border border-border p-3"><p className="text-xs text-muted-foreground">Análises IA (mês)</p><p className="font-mono-data font-semibold mt-1">{fmt(use.ai_monthly, lim.ai_monthly)}</p></div>
        <div className="rounded-md border border-border p-3"><p className="text-xs text-muted-foreground">Teleconsultas (mês)</p><p className="font-mono-data font-semibold mt-1">{fmt(use.teleconsults_monthly, lim.teleconsults_monthly)}</p></div>
      </div>
    </Card>
  );
}

function compressLogo(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const max = 300;
      let { width, height } = img;
      const scale = max / Math.max(width, height);
      if (scale < 1) { width = Math.round(width * scale); height = Math.round(height * scale); }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("imagem inválida")); };
    img.src = url;
  });
}

export default function Settings() {
  const fileRef = useRef();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ name: "", cnpj: "", address: "", phone: "", crm_default: "", logo: "", memed_token: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/clinic-profile").then((r) => {
      const d = r.data || {};
      setF({ name: d.name || "", cnpj: d.cnpj || "", address: d.address || "", phone: d.phone || "", crm_default: d.crm_default || "", logo: d.logo || "", memed_token: d.memed_token || "" });
    }).catch(() => {});
  }, []);

  const onLogo = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Use JPG, PNG ou WEBP"); return; }
    try { const data = await compressLogo(file); setF((p) => ({ ...p, logo: data })); }
    catch { toast.error("Não foi possível processar o logótipo"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/clinic-profile", f);
      toast.success("Perfil da clínica salvo");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 sm:p-10 max-w-3xl" data-testid="settings-page">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-ai mb-2">Configurações</p>
        <h1 className="font-heading text-4xl font-light tracking-tighter">Perfil da clínica</h1>
        <p className="text-muted-foreground text-sm mt-1">Estes dados aparecem no cabeçalho das receitas, atestados, pedidos e laudos.</p>
      </div>

      <PlanCard />

      <Card className="p-6 border-border shadow-none space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 rounded-lg overflow-hidden bg-secondary flex items-center justify-center shrink-0 border border-border">
            {f.logo ? <img src={f.logo} alt="logo" className="w-full h-full object-contain" data-testid="clinic-logo-preview" />
              : <Building2 className="w-8 h-8 text-muted-foreground" />}
          </div>
          <div>
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="clinic-logo-btn">
              <Camera className="w-4 h-4 mr-2" /> {f.logo ? "Trocar logótipo" : "Adicionar logótipo"}
            </Button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
              onChange={(e) => onLogo(e.target.files[0])} data-testid="clinic-logo-input" />
            <p className="text-xs text-muted-foreground mt-2">PNG com fundo transparente fica melhor.</p>
          </div>
        </div>

        <div className="space-y-2"><Label>Nome da clínica / consultório</Label>
          <Input value={f.name} onChange={set("name")} placeholder="Clínica LIA" data-testid="clinic-name-input" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>CNPJ</Label>
            <Input value={f.cnpj} onChange={set("cnpj")} data-testid="clinic-cnpj-input" /></div>
          <div className="space-y-2"><Label>Telefone</Label>
            <Input value={f.phone} onChange={set("phone")} data-testid="clinic-phone-input" /></div>
        </div>
        <div className="space-y-2"><Label>Endereço</Label>
          <Input value={f.address} onChange={set("address")} placeholder="Rua, nº, cidade" data-testid="clinic-address-input" /></div>
        <div className="space-y-2"><Label>CRM padrão do médico</Label>
          <Input value={f.crm_default} onChange={set("crm_default")} placeholder="Ex: CRM-SP 123456" data-testid="clinic-crm-input" /></div>

        <Button onClick={save} disabled={saving} data-testid="save-clinic-btn">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar perfil
        </Button>
      </Card>

      <Card className="p-6 border-border shadow-none space-y-4 mt-6" data-testid="integrations-card">
        <div className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-ai" />
          <div>
            <p className="font-heading font-medium">Integrações</p>
            <p className="text-xs text-muted-foreground">Conecte serviços externos à sua conta.</p>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Token Memed (prescrição digital)</Label>
          <Input value={f.memed_token} onChange={set("memed_token")} type="password"
            placeholder="Cole aqui o seu token de parceiro Memed" data-testid="memed-token-input" />
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <FileText className="w-3 h-3" /> Guardamos o token com segurança na sua conta. A prescrição via Memed é ativada assim que o token for validado.
            <a href="https://memed.com.br" target="_blank" rel="noopener noreferrer" className="text-ai inline-flex items-center gap-0.5 hover:underline">
              Obter token <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
        <Button onClick={save} disabled={saving} variant="outline" data-testid="save-integrations-btn">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar integrações
        </Button>
      </Card>

      <p className="text-xs text-muted-foreground italic mt-4 flex items-center gap-2">
        <FileText className="w-3 h-3" /> O CRM padrão é usado nos documentos quando não é informado um CRM específico.
      </p>
    </div>
  );
}
